import {
  collection,
  doc,
  addDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { assertNotImpersonating } from '../utils/impersonationGuard';
import EmailService from './emailService';
import SystemEmailService from './systemEmailService';

export interface EmailVerification {
  id: string;
  email: string;
  userId: string;
  workspaceId?: string; // Optional for backward compatibility
  verificationCode: string;
  status: 'pending' | 'verified' | 'expired';
  createdAt: Timestamp;
  verifiedAt?: Timestamp;
  expiresAt: Timestamp;
  attempts: number;
  maxAttempts: number;
}

class EmailVerificationService {
  private collectionName = 'emailVerifications';

  /** Whether an email is already registered as a Scubamail login (server-side check). */
  async checkEmailRegisteredAsAccount(
    email: string,
    currentUserEmail?: string | null
  ): Promise<{ exists: boolean; error?: string }> {
    const normalized = email.trim().toLowerCase();
    if (!normalized.includes('@')) {
      return { exists: false };
    }

    if (currentUserEmail && currentUserEmail.toLowerCase() === normalized) {
      return { exists: true };
    }

    try {
      const checkFn = httpsCallable<{ email: string }, { exists: boolean }>(
        functions,
        'checkSenderEmailRegistered'
      );
      const result = await checkFn({ email: normalized });
      return { exists: Boolean(result.data?.exists) };
    } catch (error) {
      console.warn('checkSenderEmailRegistered unavailable, skipping cross-account check:', error);
      return { exists: false };
    }
  }

  // Generate a 6-digit verification code
  private generateVerificationCode(): string {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  // Create a new email verification
  async createEmailVerification(email: string, userId: string, workspaceId?: string, userName?: string): Promise<string> {
    assertNotImpersonating('verify sender email');
    try {
      const verificationCode = this.generateVerificationCode();
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 15 * 60 * 1000); // 15 minutes from now

      const verificationData: Omit<EmailVerification, 'id'> = {
        email: email.toLowerCase(),
        userId,
        verificationCode,
        status: 'pending',
        createdAt: Timestamp.fromDate(now),
        expiresAt: Timestamp.fromDate(expiresAt),
        attempts: 0,
        maxAttempts: 3,
        // Only set when present. Firestore rejects `undefined` field values
        // outright ("Unsupported field value: undefined"), so an optional field
        // must be omitted rather than assigned undefined — spreading it in
        // conditionally is the only safe way to keep it optional.
        ...(workspaceId ? { workspaceId } : {})
      };

      const docRef = await addDoc(collection(db, this.collectionName), verificationData);
      console.log('Created email verification with ID:', docRef.id);

      // Send verification email
      await this.sendVerificationEmail(email, verificationCode, userName);

      return docRef.id;
    } catch (error) {
      console.error('Error creating email verification:', error);
      const reason = error instanceof Error ? error.message : String(error);
      throw new Error(reason.startsWith('Failed to send verification email')
        ? reason
        : `Failed to create email verification: ${reason}`);
    }
  }

  // Send verification email
  private async sendVerificationEmail(email: string, verificationCode: string, userName?: string): Promise<void> {
    try {
      const displayUserName = userName || 'User';

      // Check for Firestore override first (same pattern as user_verification / password_reset)
      let subject = 'Verify Sender Email Address - Scubamail';
      let htmlBody = SystemEmailService.getPasscodeEmail(displayUserName, verificationCode, 'sender_verification');
      let fromName = 'Scubamail';

      try {
        const { platformEmailService } = await import('./platformEmailService');
        const automation = await platformEmailService.getSystemAutomation('sender_verification');
        if (automation && automation.steps && automation.steps.length > 0) {
          const actionStep = automation.steps.find((s: any) => s.type === 'action');
          const cfg = actionStep?.config as any;
          const innerCfg = cfg?.config ?? cfg; // handle both config.config and config shapes
          if (innerCfg?.htmlBody) {
            console.log('📧 ✅ Using Firestore override for Sender Verification email');
            const processTemplate = (tpl: string, vars: Record<string, string>) =>
              Object.entries(vars).reduce(
                (acc, [k, v]) => acc.replace(new RegExp(`{{${k}}}`, 'g'), v || ''),
                tpl
              );
            const vars = { first_name: displayUserName, passcode: verificationCode, sender_email: email };
            subject = processTemplate(innerCfg.subject || subject, vars);
            htmlBody = processTemplate(innerCfg.htmlBody, vars);
            fromName = innerCfg.fromName || fromName;
          }
        }
      } catch (overrideErr) {
        console.warn('📧 Sender verification Firestore override fetch failed, using default:', overrideErr);
      }

      const textBody = `Verify Sender Email Address - Scubamail\n\nTo verify this email address for sending campaigns, please use the verification code below:\n\nVERIFICATION CODE: ${verificationCode}\n\nIMPORTANT: This code will expire in 15 minutes for security reasons.`;

      const sendResult = await EmailService.sendEmail({
        to: email,
        subject,
        htmlBody,
        textBody,
        fromName,
        tags: { Source: 'system', Type: 'sender_verification' }
      });

      if (!sendResult?.success) {
        throw new Error(sendResult?.error || 'Email provider rejected the verification email');
      }

      console.log('Verification email sent to:', email);
    } catch (error) {
      console.error('Error sending verification email:', error);
      const reason = error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to send verification email: ${reason}`);
    }
  }

  // Generate HTML email template
  private generateVerificationEmailHTML(verificationCode: string): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Verify Your Email</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
          .content { background: #f9f9f9; padding: 30px; border-radius: 0 0 10px 10px; }
          .code { background: #fff; border: 2px dashed #667eea; padding: 20px; text-align: center; margin: 20px 0; border-radius: 8px; }
          .verification-code { font-size: 32px; font-weight: bold; color: #667eea; letter-spacing: 5px; }
          .button { display: inline-block; background: #667eea; color: white; padding: 12px 30px; text-decoration: none; border-radius: 5px; margin: 20px 0; }
          .footer { text-align: center; margin-top: 30px; color: #666; font-size: 14px; }
          .warning { background: #fff3cd; border: 1px solid #ffeaa7; padding: 15px; border-radius: 5px; margin: 20px 0; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🔐 Verify Your Email Address</h1>
            <p>Welcome to Scubamail!</p>
          </div>
          <div class="content">
            <h2>Almost there!</h2>
            <p>To complete your email verification, please use the verification code below:</p>
            
            <div class="code">
              <div class="verification-code">${verificationCode}</div>
              <p style="margin: 10px 0 0 0; color: #666;">Enter this code in the verification page</p>
            </div>

            <div class="warning">
              <strong>⚠️ Important:</strong> This code will expire in 15 minutes for security reasons.
            </div>

            <p>If you didn't request this verification, please ignore this email.</p>
            
            <p>Best regards,<br>The Scubamail Team</p>
          </div>
          <div class="footer">
            <p>This email was sent by Scubamail. If you have any questions, please contact our support team.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  // Generate text email template
  private generateVerificationEmailText(verificationCode: string): string {
    return `
Verify Your Email Address - Scubamail

Welcome to Scubamail!

To complete your email verification, please use the verification code below:

VERIFICATION CODE: ${verificationCode}

Enter this code in the verification page to complete your registration.

IMPORTANT: This code will expire in 15 minutes for security reasons.

If you didn't request this verification, please ignore this email.

Best regards,
The Scubamail Team

---
This email was sent by Scubamail. If you have any questions, please contact our support team.
    `;
  }

  // Verify email with code
  async verifyEmailWithCode(email: string, verificationCode: string, userId: string): Promise<{ success: boolean; message: string }> {
    assertNotImpersonating('confirm sender email');
    try {
      // Find the verification record
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email.toLowerCase()),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return { success: false, message: 'No pending verification found for this email.' };
      }

      const verificationDoc = querySnapshot.docs[0];
      const verificationData = verificationDoc.data() as EmailVerification;

      // Check if expired
      const now = new Date();
      const expiresAt = verificationData.expiresAt.toDate();

      if (now > expiresAt) {
        // Mark as expired
        await updateDoc(doc(db, this.collectionName, verificationDoc.id), {
          status: 'expired'
        });
        return { success: false, message: 'Verification code has expired. Please request a new one.' };
      }

      // Check attempts
      if (verificationData.attempts >= verificationData.maxAttempts) {
        return { success: false, message: 'Too many failed attempts. Please request a new verification code.' };
      }

      // Check if code matches
      if (verificationData.verificationCode !== verificationCode) {
        // Increment attempts
        await updateDoc(doc(db, this.collectionName, verificationDoc.id), {
          attempts: verificationData.attempts + 1
        });

        const remainingAttempts = verificationData.maxAttempts - (verificationData.attempts + 1);
        return {
          success: false,
          message: `Invalid verification code. ${remainingAttempts} attempts remaining.`
        };
      }

      // Mark as verified
      await updateDoc(doc(db, this.collectionName, verificationDoc.id), {
        status: 'verified',
        verifiedAt: Timestamp.now()
      });

      return { success: true, message: 'Email verified successfully!' };

    } catch (error) {
      console.error('Error verifying email:', error);
      return { success: false, message: 'Failed to verify email. Please try again.' };
    }
  }

  // Resend verification code
  //
  // `workspaceId` is forwarded so the replacement verification keeps the same
  // workspace scoping as the original. It previously passed `undefined`
  // explicitly, which both crashed the write and would have produced an
  // unscoped document that workspace-filtered lookups could not find.
  async resendVerificationCode(
    email: string,
    userId: string,
    userName?: string,
    workspaceId?: string
  ): Promise<{ success: boolean; message: string }> {
    assertNotImpersonating('resend sender verification');
    try {
      // Delete existing pending verifications for this email
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email.toLowerCase()),
        where('userId', '==', userId),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Create new verification
      await this.createEmailVerification(email, userId, workspaceId, userName);

      return { success: true, message: 'New verification code sent!' };

    } catch (error) {
      console.error('Error resending verification code:', error);
      const reason = error instanceof Error ? error.message : String(error);
      return { success: false, message: reason || 'Failed to resend verification code. Please try again.' };
    }
  }

  // Check if email is verified
  async isEmailVerified(email: string, userId: string): Promise<boolean> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('email', '==', email.toLowerCase()),
        where('userId', '==', userId),
        where('status', '==', 'verified')
      );

      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;

    } catch (error) {
      console.error('Error checking email verification status:', error);
      return false;
    }
  }

  // Get all verified emails for a user (optionally filtered by workspace)
  async getUserVerifiedEmails(userId: string, workspaceId?: string, includeAllStatuses = false): Promise<EmailVerification[]> {
    try {
      const constraints = [where('userId', '==', userId)] as any[];
      if (workspaceId) {
        constraints.push(where('workspaceId', '==', workspaceId));
      }

      if (!includeAllStatuses) {
        constraints.push(where('status', '==', 'verified'));
      }

      const q = query(collection(db, this.collectionName), ...constraints);

      const querySnapshot = await getDocs(q);
      const verifications: EmailVerification[] = [];

      querySnapshot.forEach((doc) => {
        verifications.push({
          id: doc.id,
          ...doc.data()
        } as EmailVerification);
      });

      return verifications;

    } catch (error) {
      console.error('Error getting user verified emails:', error);
      return [];
    }
  }

  // Delete a verified email
  async deleteVerifiedEmail(verificationId: string, userId: string): Promise<void> {
    assertNotImpersonating('delete verified sender');
    try {
      // First verify that this verification belongs to the user
      const verificationDoc = await getDoc(doc(db, this.collectionName, verificationId));

      if (!verificationDoc.exists()) {
        throw new Error('Email verification not found');
      }

      const verificationData = verificationDoc.data() as EmailVerification;

      if (verificationData.userId !== userId) {
        throw new Error('Unauthorized to delete this email verification');
      }

      // Delete the verification document
      await deleteDoc(doc(db, this.collectionName, verificationId));

    } catch (error) {
      console.error('Error deleting verified email:', error);
      throw error;
    }
  }
}

export const emailVerificationService = new EmailVerificationService();
